/* assets/benefits.js (fixed + hardened)
   - evita executar 2x mesmo se o arquivo for injetado duplicado
   - protege Safari antigo (MediaQueryList)
   - protege JSON.parse attrs
   - protege Motion/animation
   - protege Swiper
   - protege seletores ausentes
   - evita listeners duplicados em details
*/
(() => {
  if (window.__ccBenefitsLoaded) return;
  window.__ccBenefitsLoaded = true;

  const on = (el, ev, fn, opts) => el && el.addEventListener && el.addEventListener(ev, fn, opts);
  const off = (el, ev, fn, opts) => el && el.removeEventListener && el.removeEventListener(ev, fn, opts);

  class Benefits extends HTMLElement {
    constructor() {
      super();

      this.templateElement = this.querySelector("[benefit-content]");
      this._template = this.templateElement?.content?.cloneNode(true) || null;

      this._animationContainer = this.querySelector("[animation-container]");
      this._numberOfBlocks = parseInt(this.getAttribute("number-of-blocks"), 10) || 0;

      this.mediaQuery = window.matchMedia("(max-width: 991px)");

      this.onlyMobileSlider = null;

      this.mainSliderElement = this.querySelector("[main-slider]");
      this.slideProgressBar = this.querySelector("[slide-progress]");

      try {
        this.responsive_breakpoints = JSON.parse(
          this.getAttribute("slide-responsive-breakpoints") || "{}"
        );
      } catch (_) {
        this.responsive_breakpoints = {};
      }

      this.slidePaginationType = this.getAttribute("slide-pagination-type") || null;
      this.sliderPerView = this.getAttribute("slide-per-view") || "1";

      this.resizeTimeout = null;

      // binds
      this.handleResize = this.handleResize.bind(this);
      this._mqHandler = (e) => this.handleViewportChange(!!e.matches);
      this._mutationObserver = null;
    }

    static get isMobile() {
      return window.innerWidth <= 991;
    }

    connectedCallback() {
      // Media query change (Safari fallback)
      if (this.mediaQuery?.addEventListener) {
        this.mediaQuery.addEventListener("change", this._mqHandler);
      } else if (this.mediaQuery?.addListener) {
        this.mediaQuery.addListener(this._mqHandler);
      }

      on(window, "resize", this.handleResize);

      this.handleViewportChange(Benefits.isMobile);

      // benefit-card open/close => is-expanded (evita duplicar listener)
      const benefitCards = this.querySelectorAll("[benefit-card]");
      benefitCards.forEach((card) => {
        const details = card.querySelector("details[accordionTab]");
        if (!details) return;

        // estado inicial
        card.classList.toggle("is-expanded", details.hasAttribute("open"));

        // evita registrar 2x
        if (details.__ccToggleBound) return;
        details.__ccToggleBound = true;

        on(details, "toggle", () => {
          card.classList.toggle("is-expanded", !!details.open);
          this.immediateSliderUpdate();
          setTimeout(() => this.forceUpdateSliderHeight(), 200);
        });
      });

      // garante altura inicial
      setTimeout(() => this.forceUpdateSliderHeight(), 200);

      // opcional: observer para quando tema trocar HTML do slider
      this._mutationObserver = new MutationObserver(() => {
        // não re-inicializa toda hora, só garante altura
        this.immediateSliderUpdate();
      });
      this._mutationObserver.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      off(window, "resize", this.handleResize);
      clearTimeout(this.resizeTimeout);

      if (this.mediaQuery?.removeEventListener) {
        this.mediaQuery.removeEventListener("change", this._mqHandler);
      } else if (this.mediaQuery?.removeListener) {
        this.mediaQuery.removeListener(this._mqHandler);
      }

      if (this._mutationObserver) {
        this._mutationObserver.disconnect();
        this._mutationObserver = null;
      }
    }

    handleResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.forceUpdateSliderHeight();
      }, 250);
    }

    handleViewportChange(isMobile) {
      if (isMobile) {
        setTimeout(() => {
          // se o carrossel for usado no mobile
          if (this.getAttribute("carousel-enabled") === "true") {
            this.detailToggleUpdateSlider();
          }
          this.reInitSlider();
        }, 100);

        // se não tem carrossel mas ainda tem details no mobile
        if (this.getAttribute("carousel-enabled") === "false") {
          this.detailToggleUpdateSlider();
        }
      } else {
        setTimeout(() => this.forceUpdateSliderHeight(), 50);
      }

      setTimeout(() => this.forceUpdateSliderHeight(), 100);

      this.checkContainerInView();
    }

    reInitSlider() {
      // se já existe, só atualiza
      if (this.onlyMobileSlider?.initialized) {
        try {
          this.onlyMobileSlider.update();
        } catch (_) {}
        setTimeout(() => this.forceUpdateSliderHeight(), 50);
        return;
      }

      // senão, inicializa
      this.enableSwiper();
      setTimeout(() => this.forceUpdateSliderHeight(), 50);
    }

    detailToggleUpdateSlider() {
      const detailsElements = this.mainSliderElement?.querySelectorAll("details");
      if (!detailsElements) return;

      detailsElements.forEach((detail) => {
        // evita duplicar listener
        if (detail.__ccClickBound) return;
        detail.__ccClickBound = true;

        on(detail, "click", () => {
          detail.classList.toggle("is-open");
          this.immediateSliderUpdate();
          setTimeout(() => this.forceUpdateSliderHeight(), 200);
        });
      });
    }

    forceUpdateSliderHeight() {
      this.updateSliderHeight();
      if (this.onlyMobileSlider?.initialized) {
        try {
          this.onlyMobileSlider.update();
        } catch (_) {}
      }
    }

    immediateSliderUpdate() {
      this.updateSliderHeight();
      if (this.onlyMobileSlider?.initialized) {
        try {
          this.onlyMobileSlider.update();
        } catch (_) {}
      }
    }

    updateSliderHeight() {
      if (!this.onlyMobileSlider || !this.mainSliderElement) return;
      const slides = this.onlyMobileSlider.slides;
      const idx = this.onlyMobileSlider.activeIndex;

      const activeSlide = slides?.[idx];
      if (!activeSlide) return;

      const accordionBody = activeSlide.querySelector("[accordionbody]");
      const accordionSummary = activeSlide.querySelector("summary");
      if (!accordionBody || !accordionSummary) return;

      const accordionBodyHeight = accordionBody.offsetHeight || 0;
      const accordionSummaryHeight = accordionSummary.offsetHeight || 0;

      const openHeight = accordionBodyHeight + accordionSummaryHeight;
      const closeHeight = accordionSummaryHeight;

      const details = activeSlide.querySelector("details");
      const isOpen = !!(details && details.classList.contains("is-open"));
      const wrapperHeight = isOpen ? openHeight : closeHeight;

      const swiperWrapper = this.querySelector(".swiper-wrapper");
      if (swiperWrapper) swiperWrapper.classList.toggle("dropdown-open", isOpen);

      const benefitsSliderContent = this.querySelector("[benefits-slider-content]");
      if (benefitsSliderContent) benefitsSliderContent.style.height = `${wrapperHeight}px`;
    }

    checkContainerInView() {
      // só tenta animar se Motion existir e se "animation" estiver ativo no tema
      if (!window.animation) return;
      if (typeof Motion === "undefined" || !Motion?.inView || !Motion?.animate) return;
      if (!this._animationContainer) return;

      Motion.inView(
        this._animationContainer,
        this.animateBenefits.bind(this),
        { amount: Benefits.isMobile ? 0.01 : 0.2, once: true }
      );
    }

    animateBenefits() {
      if (typeof Motion === "undefined" || !Motion?.animate) return;

      const oddCards = this.querySelectorAll(
        "[benefits-desktop-content] [benefit-card]:nth-child(odd) [animate-card]"
      );
      const evenCards = this.querySelectorAll(
        "[benefits-desktop-content] [benefit-card]:nth-child(even) [animate-card]"
      );
      const sliderCards = this.querySelectorAll(
        "[benefits-slider-content] [animate-card]"
      );
      const desktopCards = this.querySelectorAll(
        "[benefits-desktop-content] [animate-card]"
      );

      const allCards = sliderCards.length > 0 ? sliderCards : desktopCards;

      this._innerCircle = this.parentElement?.querySelector("[animation-inner-circle]") || null;

      if (Benefits.isMobile) {
        this.cardAnimation(allCards, null, "mobile");
      } else {
        this.cardAnimation(oddCards, "odd", "desktop");
        this.cardAnimation(evenCards, "even", "desktop");
      }
    }

    enableSwiper() {
      if (!this.mainSliderElement) return;
      if (typeof Swiper === "undefined") return;

      this.onlyMobileSlider = new Swiper(this.mainSliderElement, {
        spaceBetween: 20,
        autoHeight: true,
        slidesPerView: this.sliderPerView,
        pagination: {
          el: this.slideProgressBar,
          type: this.slidePaginationType,
          clickable: true,
        },
        on: {
          init: () => setTimeout(() => this.forceUpdateSliderHeight(), 100),
          slideChange: () => setTimeout(() => this.forceUpdateSliderHeight(), 50),
          slideChangeTransitionEnd: () => this.forceUpdateSliderHeight(),
        },
      });
    }

    cardAnimation(elements, oddOrEven, mode = "desktop") {
      if (typeof Motion === "undefined" || !Motion?.animate) return;
      if (!elements || !elements.forEach) return;

      const isMobile = mode === "mobile";
      const easing = "linear";

      elements.forEach((element, index) => {
        if (!element) return;

        let xOffset = isMobile ? "100px" : (oddOrEven === "even" ? "-95px" : "95px");
        let cardDelay = 0.1;

        if (!isMobile && index === 1 && this._numberOfBlocks > 4) {
          xOffset = oddOrEven === "even" ? "-150px" : "150px";
          cardDelay = 0;
        }

        const contentItems = element.querySelectorAll("[animation-card-content]");
        const innerCircle = element.querySelector(".inner-circle") || this._innerCircle;

        const combinedSequence = Benefits.isMobile
          ? [
              [
                innerCircle,
                {
                  opacity: [0, 1],
                  scale: [0.3, 1],
                  borderColor: ["var(--color-background)", "var(--circle-border)"],
                },
                { duration: 0.5, ease: easing },
              ],
              [
                element,
                { opacity: [0, 1], y: [xOffset, 0] },
                { duration: 0.5, ease: "ease-in", delay: Motion.stagger(0.2) },
              ],
              [
                "[animate-fade]",
                { opacity: [0, 1] },
                { duration: 0.5, ease: "ease-in", delay: Motion.stagger(0.2) },
              ],
            ]
          : [
              [element, { opacity: 0, x: xOffset }, { duration: 0, ease: easing }],
              [
                element,
                { opacity: [0, 1], scale: [0, 1] },
                { duration: 0.5, ease: easing, at: "0", delay: Motion.stagger(0.2) },
              ],
              [
                contentItems,
                { opacity: [0, 1], y: ["30%", "0"] },
                { duration: 0.3, ease: easing, delay: Motion.stagger(0.2), at: "0" },
              ],
              [
                element,
                { x: 0 },
                { duration: 0.5, ease: "ease-in", at: "-0.5", delay: cardDelay },
              ],
              [
                innerCircle,
                { opacity: [0, 1], scale: [0.3, 1] },
                { duration: 0.3, ease: easing, at: "-0.3" },
              ],
              [
                this._animationContainer,
                { borderColor: ["var(--color-background)", "var(--circle-border)"] },
                { duration: 0.4, ease: easing, at: "0.4" },
              ],
            ];

        Motion.animate(combinedSequence);
      });
    }
  }

  // não registra duas vezes
  if (!customElements.get("benefits-component")) {
    customElements.define("benefits-component", Benefits);
  }

  class ShowButton extends HTMLElement {
    constructor() {
      super();
      this.btn = this.querySelector("[toggle-text-button]");
      this.text = this.querySelector("[text-content]");
      this.extraText = this.querySelector("[read-more-extra]");
      this.shortTextHeight = 0;

      this.resizeTimeout = null;

      // arrow function (não precisa bind e não quebra)
      this.handleResize = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (!this.text) return;

          this.calculateShortTextHeight();

          if (!this.text.classList.contains("active")) {
            this.text.style.height = this.shortTextHeight + "px";
          }

          const benefitsComponent = this.closest("benefits-component");
          if (benefitsComponent) benefitsComponent.forceUpdateSliderHeight();
        }, 250);
      };
    }

    connectedCallback() {
      // se não tem o bloco, não faz nada (evita quebrar)
      if (!this.text) return;

      this.calculateShortTextHeight();
      this.setupEventListeners();

      // add resize (passive)
      on(window, "resize", this.handleResize, { passive: true });
    }

    disconnectedCallback() {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      off(window, "resize", this.handleResize);
    }

    calculateShortTextHeight() {
      if (!this.text) return;

      const originalHeight = this.text.style.height;
      this.text.style.height = "auto";
      this.shortTextHeight = this.text.offsetHeight || 0;
      this.text.style.height = originalHeight || this.shortTextHeight + "px";
    }

    setupEventListeners() {
      if (!this.btn) return;

      // evita duplicar listener caso o tema re-hidrate
      if (this.btn.__ccClickBound) return;
      this.btn.__ccClickBound = true;

      on(this.btn, "click", (e) => {
        e.preventDefault();
        if (!this.text) return;

        // fecha os outros
        document.querySelectorAll("show-button").forEach((sb) => {
          if (sb === this) return;
          const t = sb.querySelector("[text-content]");
          const card = sb.closest("[benefit-card]");
          if (t && t.classList.contains("active")) {
            t.classList.remove("active");
            t.style.height = (sb.shortTextHeight || t.offsetHeight || 0) + "px";
            card?.classList.remove("is-expanded");
          }
        });

        this.text.classList.toggle("active");

        if (this.text.classList.contains("active")) {
          this.closest("[benefit-card]")?.classList.add("is-expanded");
          this.text.style.height = (this.text.scrollHeight || 0) + "px";
        } else {
          this.closest("[benefit-card]")?.classList.remove("is-expanded");
          this.text.style.height = this.shortTextHeight + "px";
        }

        const benefitsComponent = this.closest("benefits-component");
        if (benefitsComponent) {
          benefitsComponent.immediateSliderUpdate();
          setTimeout(() => benefitsComponent.forceUpdateSliderHeight(), 200);
        }
      });
    }
  }

  // não registra duas vezes
  if (!customElements.get("show-button")) {
    customElements.define("show-button", ShowButton);
  }
})();
